import { Button, Checkbox, Form, Input, Spin, Upload } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSalesStatusEnum } from '../../../enums/FileSalesStatusEnum';
import type File from '../../../interfaces/File';
import type { SellFilePayload } from '../../../interfaces/FileApiService';
import type User from '../../../interfaces/User';
import { FileService } from '../../../services/fileApiService';
import { copyToClipboard, getPurchaseLink, printAmount } from '../../../utils/utils';
import { Icon } from '../../Core/Icon';

interface Props {
  file: File;
  authenticatedUser: User | null;
  onSubmit?: (file: File) => void;
  onCancel: () => void;
  onRemoveFromStore: (file: File) => void;
  onClose: (file: File) => void;
}

interface FormState {
  title: string;
  description: string;
  currentPrice: number | string;
  isUnsealed?: boolean;
  currencyPrice: string;
  thumbnail?: string;
}

export const FileSellForm = ({ file, onSubmit, onCancel, onRemoveFromStore }: Props) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState | undefined>();

  const fileService = new FileService();

  const [isSold, setIsSold] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [loadingFile, setLoadingFile] = useState(true);
  const [purchaseLink, setPurchaseLink] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const sellFile = (payload: SellFilePayload) => {
    if (!form) {
      return;
    }
    return fileService
      .sellFile(payload)
      .then(() => {
        if (onSubmit) {
          onSubmit({
            ...file,
            fileName: form.title,
            salesStatus: FileSalesStatusEnum.ForSale,
          });
        }
        setIsSold(true);
        setPurchaseLink(getPurchaseLink(file.id));
      })
      .catch(() => {
        setErrorMessage(t('errors.genericSellError'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * Handles the submission of form data, processes and transforms it into a `User` object,
   * and passes the constructed user object to the given submission handler.
   *
   * @param {any} values - The form values submitted by the user. Includes fields such as
   * firstName, lastName, email, role, expirationDate, and privateUser.
   */
  const handleSubmit = () => {
    if (!form) {
      return;
    }
    setLoading(true);

    const payload: SellFilePayload = {
      fileId: file.id,
      Title: form.title,
      Description: form.description,
      CurrentPrice: form.currentPrice,
      IsUnsealed: form.isUnsealed || false,
      CurrencyPrice: form.currencyPrice || 'EUR',
      thumbnail: form.thumbnail,
    };
    const formData = new FormData();

    if (fileList.length > 0) {
      formData.append('file', fileList[0]);
      fileService
        .uploadSalesImage(file.id, formData)
        .then((res) => {
          sellFile({ ...payload, thumbnail: res.url });
        })
        .catch(() => {
          setErrorMessage(t('modal.fileSellForm.previewUploadError'));
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      sellFile(payload);
    }
  };

  const handleRemoveFromStore = async () => {
    try {
      setLoading(true);
      await fileService.removeFileFromStore(file.id);
      file.salesStatus = FileSalesStatusEnum.OnHold;
      onRemoveFromStore(file);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleFormFieldChange = (field: keyof FormState, value: any) => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: value,
      } as FormState;
    });
  };

  const handleEdit = () => {
    setIsSold(false);
  };

  useEffect(() => {
    setLoadingFile(true);
    fileService
      .getFile(file.id)
      .then((response) => {
        setForm({
          title: response.fileName.replace(file.fileExtension, ''),
          description: response.description,
          currentPrice: response.price === 0 ? '' : response.price,
          isUnsealed: response.isUnsealed,
          currencyPrice: response.currency || 'EUR',
          thumbnail: response.publicThumbnail || '',
        });

        if (response.publicThumbnail) {
          setPreviewImage(response.publicThumbnail);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoadingFile(false);
      });
  }, [file]);

  if (isSold) {
    return (
      <div className="pt-6">
        <p className="text-green-dark mb-2 font-semibold">
          {t('modal.fileSellForm.listedSuccess')}
        </p>
        {purchaseLink && (
          <Form.Item label={t('modal.fileSellForm.purchaseLink')}>
            <Input
              value={purchaseLink}
              readOnly
              addonAfter={
                <div className="flex items-center gap-2">
                  <Button
                    type="link"
                    size="small"
                    className="text-blue-dark! hover:text-azure! h-[22px] p-0"
                    onClick={() => copyToClipboard(purchaseLink)}
                  >
                    <Icon name="CopyIcon" className="w-4" />
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    className="text-blue-dark! hover:text-azure! h-[22px] p-0"
                    onClick={() => window.open(purchaseLink, '_blank')}
                  >
                    <Icon name="ExternalLinkIcon" className="w-4" />
                  </Button>
                </div>
              }
            />
          </Form.Item>
        )}

        <div className="mt-4 flex flex-row items-center justify-end gap-2">
          <Button size="middle" onClick={handleEdit} disabled={loading}>
            {t('common.edit')}
          </Button>
          <Button size="middle" onClick={onCancel} disabled={loading}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    );
  }

  return loadingFile ? (
    <div className="flex h-full w-full items-center justify-center">
      <Spin size="large" />
    </div>
  ) : (
    <Form
      layout="vertical"
      size="large"
      className="pt-6 [&_.ant-form-item]:mb-2 [&_.ant-form-item-label]:hidden"
      onFinish={handleSubmit}
      initialValues={form}
    >
      <Form.Item
        label={t('modal.fileSellForm.previewImage')}
        className="mb-4 flex w-full items-center justify-center"
      >
        <div
          onDragOver={() => {
            // e.preventDefault();
            if (!isDragging) setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          className={`sell-form ${isDragging ? 'is-dragging' : ''}`}
        >
          <Upload
            listType="picture-card"
            fileList={fileList}
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              const isImage = file.type.startsWith('image/');

              return new Promise<void>((resolve, reject) => {
                if (!isImage) {
                  return reject(new Error(t('modal.fileSellForm.mustBeImage')));
                }

                if (file.size / 1024 / 1024 > 5) {
                  return reject(new Error(t('modal.fileSellForm.imageTooLarge')));
                }

                const img = new Image();
                img.src = URL.createObjectURL(file);
                img.onload = () => {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    setPreviewImage(e.target?.result as string);
                  };
                  reader.readAsDataURL(file);
                  setFileList([file]);
                  resolve();
                };

                img.onerror = () => {
                  reject(new Error(t('modal.fileSellForm.unableToReadImage')));
                };
              });
            }}
          >
            {previewImage ? (
              <div className="relative flex h-full items-center justify-center">
                <img
                  src={previewImage}
                  alt={t('modal.fileSellForm.previewAlt')}
                  className="h-full rounded-md object-contain"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewImage(null);
                    setFileList([]);
                  }}
                  className="text-blue-dark absolute top-1 right-1"
                  title={t('modal.fileSellForm.removeImage')}
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="text-gray-dark text-center">
                <div className="text-sm font-medium">{t('modal.fileSellForm.uploadPreview')}</div>
                <div className="text-gray-dark mt-1 text-xs">
                  {t('modal.fileSellForm.uploadPreviewHint')}
                </div>
              </div>
            )}
          </Upload>
        </div>
      </Form.Item>

      <Form.Item name="title" rules={[{ required: true }]}>
        <Input
          onChange={(e) => handleFormFieldChange('title', e.target.value)}
          autoFocus
          autoCapitalize="none"
          placeholder={t('common.title')}
          maxLength={255}
          addonAfter={
            file?.isFolder ? <Icon name="FolderIcon" /> : <span>{file?.fileExtension}</span>
          }
        />
      </Form.Item>
      <Form.Item name="description" rules={[{ required: false }]}>
        <TextArea
          onChange={(e) => handleFormFieldChange('description', e.target.value)}
          placeholder={t('common.description')}
        />
      </Form.Item>

      <Form.Item
        name="currentPrice"
        rules={[
          {
            validator: (_, value) => {
              if (value === undefined || value === '') {
                return Promise.reject(new Error(t('modal.fileSellForm.pleaseEnterPrice')));
              }
              const numericValue = parseFloat(value);
              if (isNaN(numericValue)) {
                return Promise.reject(new Error(t('modal.fileSellForm.invalidNumber')));
              }
              if (numericValue < 0.2) {
                return Promise.reject(
                  new Error(t('modal.fileSellForm.priceMin', { amount: printAmount(0.2) })),
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          type="number"
          min={0}
          step="0.01"
          prefix="€"
          onChange={(e) => handleFormFieldChange('currentPrice', e.target.value)}
          placeholder="0,00"
        />
      </Form.Item>

      <Form.Item name="isUnsealed" valuePropName="checked">
        <Checkbox
          checked={form?.isUnsealed || false}
          onChange={(e) => handleFormFieldChange('isUnsealed', e.target.checked)}
        >
          {t('modal.fileSellForm.allowDownload')}
        </Checkbox>
      </Form.Item>

      {errorMessage && <div className="text-red mt-2 text-sm">{errorMessage}</div>}

      <div className="mt-4 flex flex-row items-center justify-between gap-2">
        {file.salesStatus === FileSalesStatusEnum.ForSale ? (
          <Button size="middle" danger type="default" onClick={handleRemoveFromStore}>
            {t('modal.fileSellForm.removeFromStore')}
          </Button>
        ) : (
          <div />
        )}

        <div className="flex flex-row items-center gap-2">
          <Button size="middle" onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button size="middle" type="primary" htmlType="submit" loading={loading}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default FileSellForm;
